import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { useData } from '../../context/DataContext';
import { homeworksService, sessionsService, homeworkSubmissionsService, usersService, sessionRegistrationsService } from '../../services/firestoreService';
import emailService from '../../services/emailService';
import './HomeworkManagement.css';

const HomeworkManagement = () => {
  const { user } = useUser();
  const { homeworks, sessions, courses, loading } = useData();

  // Helper function to get session number (I, II, III, etc.)
  const getSessionNumber = (session) => {
    if (!session || !session.courseId) return '';
    const courseSessions = sessions
      .filter(s => s.courseId === session.courseId)
      .sort((a, b) => {
        // Sort by creation date or start date
        const dateA = a.startDate?.toDate?.() || a.startDate || a.createdAt?.toDate?.() || a.createdAt || 0;
        const dateB = b.startDate?.toDate?.() || b.startDate || b.createdAt?.toDate?.() || b.createdAt || 0;
        return new Date(dateA) - new Date(dateB);
      });
    const sessionIndex = courseSessions.findIndex(s => s.id === session.id);
    if (sessionIndex === -1) return '';
    const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
    return romanNumerals[sessionIndex] || `${sessionIndex + 1}`;
  };
  const [selectedSession, setSelectedSession] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingHomework, setEditingHomework] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSubmissions, setShowSubmissions] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [studentsMap, setStudentsMap] = useState({});
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    sessionId: '',
    sessionIds: [], // Multiple sessions
    topicId: '',
    topicName: '',
    dueDate: '',
    rewardPoints: 0,
    enabled: true,
    sendEmail: false
  });
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [emailFormData, setEmailFormData] = useState({
    subject: '',
    customMessage: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Determine which sessions to use - prioritize sessionIds array
      let targetSessionIds = [];
      if (formData.sessionIds && formData.sessionIds.length > 0) {
        targetSessionIds = formData.sessionIds;
      } else if (formData.sessionId) {
        targetSessionIds = [formData.sessionId];
      }

      console.log('💾 Saving homework', {
        sessionIds: formData.sessionIds,
        sessionIdsLength: formData.sessionIds?.length || 0,
        sessionId: formData.sessionId,
        targetSessionIds: targetSessionIds,
        targetSessionIdsLength: targetSessionIds.length,
        title: formData.title,
        dueDate: formData.dueDate
      });

      if (targetSessionIds.length === 0) {
        console.error('❌ No sessions selected');
        alert('Please select at least one session.');
        setSaving(false);
        return;
      }

      // Validate required fields
      if (!formData.title || !formData.title.trim()) {
        console.error('❌ Title is required');
        alert('Please enter a homework title.');
        setSaving(false);
        return;
      }

      if (!formData.dueDate) {
        console.error('❌ Due date is required');
        alert('Please select a due date.');
        setSaving(false);
        return;
      }

      const baseHomeworkData = {
        title: formData.title,
        description: formData.description,
        topicId: formData.topicId || null,
        topicName: formData.topicName || null,
        dueDate: formData.dueDate,
        rewardPoints: parseInt(formData.rewardPoints) || 0,
        enabled: formData.enabled !== undefined ? formData.enabled : true,
        createdBy: user.id
      };

      let homeworkId;
      if (editingHomework) {
        // For editing, find all related homeworks and update them
        console.log('✏️ Editing homework - finding related homeworks...');
        try {
          const allHomeworks = await homeworksService.getAllHomeworks();
          const relatedHomeworks = allHomeworks.filter(hw => 
            hw.title === editingHomework.title &&
            hw.description === editingHomework.description &&
            hw.dueDate === editingHomework.dueDate
          );
          
          console.log(`📝 Found ${relatedHomeworks.length} related homework(s) to update`);
          
          // Update all related homeworks
          const updatePromises = relatedHomeworks.map(hw => 
            homeworksService.updateHomework(hw.id, {
              ...baseHomeworkData,
              sessionId: hw.sessionId // Keep the original sessionId for each homework
            })
          );
          await Promise.all(updatePromises);
          console.log(`✅ Updated ${relatedHomeworks.length} homework(s)`);
          
          // If sessionIds changed, create new homeworks for new sessions and delete old ones
          const existingSessionIds = relatedHomeworks.map(hw => hw.sessionId).filter(Boolean);
          const sessionsToAdd = targetSessionIds.filter(id => !existingSessionIds.includes(id));
          const sessionsToRemove = existingSessionIds.filter(id => !targetSessionIds.includes(id));
          
          // Create new homeworks for new sessions
          if (sessionsToAdd.length > 0) {
            console.log(`➕ Creating ${sessionsToAdd.length} new homework(s) for new sessions`);
            const newHomeworkPromises = sessionsToAdd.map(sessionId =>
              homeworksService.createHomework({
                ...baseHomeworkData,
                sessionId: sessionId
              })
            );
            await Promise.all(newHomeworkPromises);
          }
          
          // Delete homeworks for removed sessions
          if (sessionsToRemove.length > 0) {
            console.log(`🗑️ Deleting ${sessionsToRemove.length} homework(s) for removed sessions`);
            const homeworksToDelete = relatedHomeworks.filter(hw => sessionsToRemove.includes(hw.sessionId));
            const deletePromises = homeworksToDelete.map(hw => homeworksService.deleteHomework(hw.id));
            await Promise.all(deletePromises);
          }
          
          homeworkId = editingHomework.id;
        } catch (error) {
          console.error('Error updating related homeworks:', error);
          // Fallback to updating just the current homework
          await homeworksService.updateHomework(editingHomework.id, {
            ...baseHomeworkData,
            sessionId: targetSessionIds[0]
          });
          homeworkId = editingHomework.id;
        }
      } else {
        // For new homework, create one for each selected session
        console.log(`📝 Creating homework for ${targetSessionIds.length} session(s):`, targetSessionIds);
        console.log('📋 Homework data:', baseHomeworkData);
        
        try {
          const homeworkPromises = targetSessionIds.map(async (sessionId, index) => {
            console.log(`  [${index + 1}/${targetSessionIds.length}] Creating homework for session: ${sessionId}`);
            try {
              const id = await homeworksService.createHomework({
                ...baseHomeworkData,
                sessionId: sessionId
              });
              console.log(`  ✅ Created homework ${id} for session ${sessionId}`);
              return id;
            } catch (error) {
              console.error(`  ❌ Failed to create homework for session ${sessionId}:`, error);
              throw error;
            }
          });
          const homeworkIds = await Promise.all(homeworkPromises);
          console.log(`✅ Successfully created ${homeworkIds.length} homework(s) with IDs:`, homeworkIds);
          homeworkId = homeworkIds[0]; // Use first ID for email sending if needed
          alert(`✅ Homework created successfully for ${targetSessionIds.length} session(s)!`);
        } catch (error) {
          console.error('❌ Error creating homeworks:', error);
          throw error;
        }
      }

      // If email is requested, show preview modal instead of auto-sending
      if (formData.sendEmail && formData.sessionId) {
        // Don't reset form yet - let user review and send from preview modal
        // Generate preview and show modal
        await handlePreviewEmail();
        setShowEmailPreview(true);
      } else {
        resetForm();
      }
    } catch (error) {
      console.error('❌ Error saving homework:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        formData: {
          sessionIds: formData.sessionIds,
          sessionId: formData.sessionId,
          title: formData.title,
          dueDate: formData.dueDate
        }
      });
      alert(`Error saving homework: ${error.message || 'Please try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  const sendHomeworkEmailToStudents = async (homeworkId, homeworkData) => {
    if (!selectedSession || selectedStudents.length === 0) return;
    
    setSendingEmail(true);
    try {
      const course = courses.find(c => c.id === selectedSession.courseId);
      const dueDate = new Date(homeworkData.dueDate);
      const homeworkUrl = `${window.location.origin}/student/classroom/${selectedSession.id}?tab=homework`;
      
      let sentCount = 0;
      for (const studentId of selectedStudents) {
        const student = availableStudents.find(s => s.id === studentId);
        if (student && student.email) {
          try {
            await emailService.sendHomeworkNotification({
              studentEmail: student.email,
              studentName: student.name || student.email?.split('@')[0] || 'Student',
              homeworkTitle: homeworkData.title,
              homeworkDescription: homeworkData.description,
              courseTitle: course?.title || selectedSession.title || 'Course',
              sessionTitle: selectedSession.title || 'Session',
              dueDate: dueDate.toLocaleDateString(),
              dueDateTime: dueDate.toLocaleString(),
              homeworkUrl: homeworkUrl,
              customMessage: emailFormData.customMessage || ''
            });
            sentCount++;
          } catch (error) {
            console.error(`Error sending email to ${student.email}:`, error);
          }
        }
      }
      
      alert(`Homework saved and email notifications sent to ${sentCount} student${sentCount !== 1 ? 's' : ''}.`);
      setShowEmailPreview(false);
      setEmailFormData({ subject: '', customMessage: '' });
    } catch (error) {
      console.error('Error sending homework emails:', error);
      alert('Homework saved, but there was an error sending email notifications. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePreviewEmail = async () => {
    // Get session from selectedSession, formData.sessionId, or first from sessionIds
    const session = selectedSession || 
      (formData.sessionId ? sessions.find(s => s.id === formData.sessionId) : null) || 
      (formData.sessionIds.length > 0 ? sessions.find(s => s.id === formData.sessionIds[0]) : null);
    
    if (!session || !formData.dueDate) {
      alert('Please select a session and set a due date first.');
      return;
    }

    const course = courses.find(c => c.id === session.courseId);
    const dueDate = new Date(formData.dueDate);
    const homeworkUrl = `${window.location.origin}/student/registered`;
    
    const previewData = {
      studentEmail: 'student@example.com',
      studentName: 'Student Name (Preview)',
      homeworkTitle: formData.title || 'Homework Assignment',
      homeworkDescription: formData.description || 'Complete the assigned homework.',
      courseTitle: course?.title || session.title || 'Course',
      sessionTitle: session.title || 'Session',
      dueDate: dueDate.toLocaleDateString(),
      dueDateTime: dueDate.toLocaleString(),
      homeworkUrl: homeworkUrl,
      customMessage: emailFormData.customMessage || ''
    };

    const html = emailService.generateHomeworkNotificationEmail(previewData);
    setPreviewHtml(html);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      sessionId: '',
      sessionIds: [],
      topicId: '',
      topicName: '',
      dueDate: '',
      rewardPoints: 0,
      enabled: true,
      sendEmail: false
    });
    setSelectedSession(null);
    setShowForm(false);
    setEditingHomework(null);
    setShowEmailPreview(false);
    setSelectedStudents([]);
    setAvailableStudents([]);
    setEmailFormData({ subject: '', customMessage: '' });
  };

  const handleEdit = async (homework) => {
    setEditingHomework(homework);
    
    // Find all related homeworks (same title, description, dueDate) to get all sessionIds
    console.log('📝 Editing homework:', homework.id);
    console.log('🔍 Finding related homeworks with same title, description, and dueDate...');
    
    let allSessionIds = [homework.sessionId].filter(Boolean);
    
    try {
      // Get all homeworks and filter for related ones
      const allHomeworks = await homeworksService.getAllHomeworks();
      const relatedHomeworks = allHomeworks.filter(hw => 
        hw.title === homework.title &&
        hw.description === homework.description &&
        hw.dueDate === homework.dueDate &&
        hw.id !== homework.id // Exclude the current one
      );
      
      console.log(`✅ Found ${relatedHomeworks.length} related homework(s)`);
      
      // Collect all sessionIds from related homeworks
      relatedHomeworks.forEach(hw => {
        if (hw.sessionId && !allSessionIds.includes(hw.sessionId)) {
          allSessionIds.push(hw.sessionId);
        }
      });
      
      console.log(`📋 All sessionIds for this homework:`, allSessionIds);
    } catch (error) {
      console.error('Error finding related homeworks:', error);
      // Fallback to just the current homework's sessionId
    }
    
    // Load the first session for student loading
    let sessionData = null;
    if (allSessionIds.length > 0) {
      try {
        sessionData = await sessionsService.getSession(allSessionIds[0]);
      } catch (error) {
        console.error('Error loading session:', error);
      }
    }
    setSelectedSession(sessionData);
    
    setFormData({
      title: homework.title,
      description: homework.description,
      sessionId: allSessionIds[0] || homework.sessionId || '',
      sessionIds: allSessionIds, // Load all sessionIds
      topicId: homework.topicId || homework.topicName || '',
      topicName: homework.topicName || homework.topicId || '',
      dueDate: homework.dueDate,
      rewardPoints: homework.rewardPoints || 0,
      enabled: homework.enabled !== undefined ? homework.enabled : true,
      sendEmail: false
    });
    setShowForm(true);
  };

  const handleSessionChange = async (sessionId) => {
    setFormData(prev => ({ 
      ...prev, 
      sessionId, 
      topicId: '',
      // Preserve sessionIds array if it exists
      sessionIds: prev.sessionIds || []
    }));
    if (sessionId) {
      try {
        const session = await sessionsService.getSession(sessionId);
        setSelectedSession(session);
        // Load students for this session
        await loadSessionStudents(sessionId);
      } catch (error) {
        console.error('Error loading session:', error);
        setSelectedSession(null);
        setAvailableStudents([]);
      }
    } else {
      setSelectedSession(null);
      setAvailableStudents([]);
      setSelectedStudents([]);
    }
  };

  const handleSessionIdsChange = async (sessionId, isChecked) => {
    let updatedSessionIds = [];
    
    setFormData(prev => {
      updatedSessionIds = isChecked
        ? [...(prev.sessionIds || []), sessionId]
        : (prev.sessionIds || []).filter(id => id !== sessionId);
      return { ...prev, sessionIds: updatedSessionIds };
    });
    
    // Load students from all selected sessions when a session is checked/unchecked
    // Use setTimeout to ensure state is updated first
    setTimeout(async () => {
      if (updatedSessionIds.length > 0) {
        if (updatedSessionIds.length === 1) {
          await loadSessionStudents(updatedSessionIds[0]);
        } else {
          await loadStudentsFromMultipleSessions(updatedSessionIds);
        }
      } else {
        setAvailableStudents([]);
        setSelectedStudents([]);
      }
    }, 100);
  };

  const loadSessionStudents = async (sessionId) => {
    try {
      console.log(`📚 Loading students for session: ${sessionId}`);
      
      // Get ALL registrations from registrations subcollection
      const allRegistrations = await sessionRegistrationsService.getSessionRegistrations(sessionId);
      console.log(`📋 Total registrations found: ${allRegistrations.length}`);
      
      // Log all registration statuses for debugging
      const statusCounts = {};
      const adminAddedRegistrations = [];
      allRegistrations.forEach(reg => {
        const status = reg.status || 'undefined';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
        
        // Track admin-added registrations (have approvedBy field)
        if (reg.approvedBy) {
          adminAddedRegistrations.push({
            id: reg.id,
            studentId: reg.studentId,
            status: reg.status,
            approvedBy: reg.approvedBy
          });
        }
      });
      console.log(`📊 Registration status breakdown:`, statusCounts);
      if (adminAddedRegistrations.length > 0) {
        console.log(`👨‍💼 Admin-added registrations found: ${adminAddedRegistrations.length}`);
        adminAddedRegistrations.forEach(reg => {
          if (reg.status !== 'active') {
            console.warn(`  ⚠️ Admin-added registration ${reg.id} for student ${reg.studentId} has status: "${reg.status}" (should be 'active')`);
          }
        });
      }
      
      // STRICTLY filter: Only include registrations with status === 'active'
      // This excludes: 'pending', 'cancelled', 'rejected', or any other status
      const activeRegistrations = allRegistrations.filter(reg => {
        const status = reg.status;
        const isActive = status === 'active';
        if (!isActive) {
          console.log(`  ⏭️ Skipping registration ${reg.id} for student ${reg.studentId} with status: "${status}"`);
        }
        return isActive;
      });
      console.log(`✅ Active registrations (after filtering): ${activeRegistrations.length}`);
      
      const studentsList = [];
      const processedStudentIds = new Set();
      
      // Process ONLY active registrations
      for (const reg of activeRegistrations) {
        try {
          // Double-check status before processing
          if (reg.status !== 'active') {
            console.warn(`⚠️ Registration ${reg.id} has non-active status: ${reg.status}, skipping`);
            continue;
          }
          
          const student = await usersService.getUser(reg.studentId);
          if (student && student.email && student.type === 'student') {
            if (!processedStudentIds.has(student.id)) {
              studentsList.push({
                id: student.id,
                name: student.name || student.email.split('@')[0],
                email: student.email,
                registrationId: reg.id,
                sessionId: sessionId
              });
              processedStudentIds.add(student.id);
              console.log(`  ✅ Added student: ${student.email} (registration: ${reg.id})`);
            } else {
              console.log(`  ⏭️ Skipping duplicate student: ${student.email}`);
            }
          } else {
            if (!student) {
              console.warn(`⚠️ Student ${reg.studentId} not found`);
            } else if (student.type !== 'student') {
              console.warn(`⚠️ User ${reg.studentId} is not a student (type: ${student.type})`);
            }
          }
        } catch (error) {
          console.warn(`⚠️ Could not load student ${reg.studentId}:`, error);
        }
      }
      
      console.log(`👥 Total students loaded: ${studentsList.length}`);
      setAvailableStudents(studentsList);
      // By default, don't select any students - let admin/coach choose
      setSelectedStudents([]);
    } catch (error) {
      console.error('❌ Error loading session students:', error);
      setAvailableStudents([]);
      setSelectedStudents([]);
    }
  };

  // Load students from multiple sessions and merge them
  const loadStudentsFromMultipleSessions = async (sessionIds) => {
    if (!sessionIds || sessionIds.length === 0) {
      setAvailableStudents([]);
      setSelectedStudents([]);
      return;
    }

    try {
      console.log(`📚 Loading students from ${sessionIds.length} session(s):`, sessionIds);
      
      const allStudentsMap = new Map(); // Use Map to avoid duplicates by student ID
      
      // Load students from each session
      for (const sessionId of sessionIds) {
        try {
          const allRegistrations = await sessionRegistrationsService.getSessionRegistrations(sessionId);
          
          // STRICTLY filter: Only include registrations with status === 'active'
          // This excludes: 'pending', 'cancelled', 'rejected', or any other status
          const activeRegistrations = allRegistrations.filter(reg => {
            const isActive = reg.status === 'active';
            if (!isActive) {
              console.log(`  ⏭️ Session ${sessionId}: Skipping registration ${reg.id} with status: "${reg.status}"`);
            }
            return isActive;
          });
          
          console.log(`  Session ${sessionId}: ${activeRegistrations.length} active registrations (out of ${allRegistrations.length} total)`);
          
          for (const reg of activeRegistrations) {
            try {
              // Double-check status before processing
              if (reg.status !== 'active') {
                console.warn(`⚠️ Registration ${reg.id} has non-active status: ${reg.status}, skipping`);
                continue;
              }
              
              const student = await usersService.getUser(reg.studentId);
              
              // Only include students (not coaches/admins) with email
              if (student && student.email && student.type === 'student') {
                // If student already exists, add this session to their list
                if (allStudentsMap.has(student.id)) {
                  const existing = allStudentsMap.get(student.id);
                  if (!existing.sessionIds) {
                    existing.sessionIds = [existing.sessionId];
                  }
                  if (!existing.sessionIds.includes(sessionId)) {
                    existing.sessionIds.push(sessionId);
                  }
                } else {
                  // Add new student
                  allStudentsMap.set(student.id, {
                    id: student.id,
                    name: student.name || student.email.split('@')[0],
                    email: student.email,
                    registrationId: reg.id,
                    sessionId: sessionId,
                    sessionIds: [sessionId]
                  });
                }
              } else {
                if (!student) {
                  console.warn(`⚠️ Student ${reg.studentId} not found for session ${sessionId}`);
                } else if (student.type !== 'student') {
                  console.warn(`⚠️ User ${reg.studentId} is not a student (type: ${student.type}) for session ${sessionId}`);
                }
              }
            } catch (error) {
              console.warn(`⚠️ Could not load student ${reg.studentId} from session ${sessionId}:`, error);
            }
          }
        } catch (error) {
          console.error(`❌ Error loading students from session ${sessionId}:`, error);
        }
      }
      
      const studentsList = Array.from(allStudentsMap.values());
      console.log(`✅ Total unique students loaded: ${studentsList.length}`);
      
      setAvailableStudents(studentsList);
      setSelectedStudents([]);
    } catch (error) {
      console.error('❌ Error loading students from multiple sessions:', error);
      setAvailableStudents([]);
      setSelectedStudents([]);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this homework?')) {
      try {
        await homeworksService.deleteHomework(id);
      } catch (error) {
        console.error('Error deleting homework:', error);
        alert('Error deleting homework. Please try again.');
      }
    }
  };

  useEffect(() => {
    // Load students map
    const loadStudents = async () => {
      const students = await usersService.getAllStudents();
      const map = {};
      students.forEach(s => {
        map[s.id] = s;
      });
      setStudentsMap(map);
    };
    loadStudents();
  }, []);

  const handleViewSubmissions = async (homework) => {
    setShowSubmissions(homework.id);
    try {
      const subs = await homeworkSubmissionsService.getAllSubmissions(homework.id);
      setSubmissions(subs);
    } catch (error) {
      console.error('Error loading submissions:', error);
      alert('Error loading submissions. Please try again.');
      setSubmissions([]);
    }
  };

  const handleGradeSubmission = async (homeworkId, submissionId, grade, feedback) => {
    try {
      await homeworkSubmissionsService.gradeSubmission(homeworkId, submissionId, grade, feedback, user.id);
      // Reload submissions
      const subs = await homeworkSubmissionsService.getAllSubmissions(homeworkId);
      setSubmissions(subs);
      alert('Submission graded successfully!');
    } catch (error) {
      console.error('Error grading submission:', error);
      alert('Error grading submission. Please try again.');
    }
  };

  return (
    <div className="homework-management">
      <div className="page-header">
        <h1>📝 Homework Management</h1>
        <button className="add-btn" onClick={() => { resetForm(); setShowForm(true); }}>
          ➕ Add New Homework
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => resetForm()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingHomework ? 'Edit Homework' : 'Create New Homework'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Homework Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>Select Session(s) *</label>
                <div style={{ 
                  border: '2px solid #e2e8f0', 
                  borderRadius: '8px', 
                  padding: '12px', 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  backgroundColor: '#f8fafc'
                }}>
                  {sessions.length === 0 ? (
                    <p style={{ color: '#64748b', fontStyle: 'italic' }}>No sessions available</p>
                  ) : (
                    sessions.map(session => {
                      const sessionNum = getSessionNumber(session);
                      const displayTitle = sessionNum ? `Session ${sessionNum}: ${session.title}` : session.title;
                      const isChecked = formData.sessionIds.includes(session.id) || formData.sessionId === session.id;
                      return (
                        <label 
                          key={session.id} 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '8px',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            marginBottom: '4px',
                            backgroundColor: isChecked ? '#e0e7ff' : 'transparent',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (!isChecked) e.currentTarget.style.backgroundColor = '#f1f5f9';
                          }}
                          onMouseLeave={(e) => {
                            if (!isChecked) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const wasChecked = isChecked;
                              const willBeChecked = e.target.checked;
                              
                              if (willBeChecked && !wasChecked) {
                                // Adding a session
                                setFormData(prev => {
                                  const currentSessionIds = prev.sessionIds || [];
                                  const newSessionIds = currentSessionIds.includes(session.id)
                                    ? currentSessionIds
                                    : [...currentSessionIds, session.id];
                                  console.log('✅ Adding session', session.id, 'New sessionIds:', newSessionIds);
                                  return { 
                                    ...prev, 
                                    sessionIds: newSessionIds,
                                    // Also set sessionId to first selected for backward compatibility
                                    sessionId: newSessionIds.length > 0 ? newSessionIds[0] : prev.sessionId
                                  };
                                });
                                // Load students if not already loaded (load directly to avoid overwriting sessionIds)
                                if (availableStudents.length === 0) {
                                  loadSessionStudents(session.id).then(async () => {
                                    try {
                                      const sessionData = await sessionsService.getSession(session.id);
                                      setSelectedSession(sessionData);
                                    } catch (error) {
                                      console.error('Error loading session:', error);
                                    }
                                  });
                                }
                              } else if (!willBeChecked && wasChecked) {
                                // Removing a session
                                setFormData(prev => {
                                  const currentSessionIds = prev.sessionIds || [];
                                  const newSessionIds = currentSessionIds.filter(id => id !== session.id);
                                  console.log('❌ Removing session', session.id, 'New sessionIds:', newSessionIds);
                                  return { 
                                    ...prev, 
                                    sessionIds: newSessionIds,
                                    // Update sessionId if it was the one being removed
                                    sessionId: prev.sessionId === session.id 
                                      ? (newSessionIds.length > 0 ? newSessionIds[0] : '')
                                      : prev.sessionId
                                  };
                                });
                              }
                            }}
                            style={{ marginRight: '10px', cursor: 'pointer' }}
                          />
                          <span>{displayTitle}</span>
                        </label>
                      );
                    })
                  )}
                </div>
                <small className="form-hint">
                  {formData.sessionIds.length > 0 || formData.sessionId 
                    ? `Selected: ${formData.sessionIds.length || (formData.sessionId ? 1 : 0)} session(s)`
                    : 'Select one or more sessions for this homework'}
                </small>
              </div>
              {selectedSession && selectedSession.courseId && (() => {
                const course = courses.find(c => c.id === selectedSession.courseId);
                return course && course.topics && course.topics.length > 0 ? (
                  <div className="form-group">
                    <label>Topic (Optional - leave empty for session-level homework)</label>
                    <select
                      value={formData.topicId}
                      onChange={(e) => setFormData({ ...formData, topicId: e.target.value, topicName: e.target.value })}
                    >
                      <option value="">No specific topic (Session-level)</option>
                      {course.topics.map((topic, idx) => (
                        <option key={idx} value={topic.name}>{topic.name}</option>
                      ))}
                    </select>
                    <small className="form-hint">Select a topic to associate this homework with a specific topic</small>
                  </div>
                ) : null;
              })()}
              <div className="form-group">
                <label>Due Date *</label>
                <input
                  type="datetime-local"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Reward Points</label>
                <input
                  type="number"
                  min="0"
                  value={formData.rewardPoints}
                  onChange={(e) => setFormData({ ...formData, rewardPoints: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
                <small className="form-hint">Points students will earn upon completion</small>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  />
                  <span>✅ Enable this homework (visible to students)</span>
                </label>
                <small className="form-hint">Uncheck to hide this homework from students</small>
              </div>
              {selectedSession && formData.sessionId && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.sendEmail}
                      onChange={(e) => {
                        setFormData({ ...formData, sendEmail: e.target.checked });
                      }}
                    />
                    <span>📧 Email students about this homework</span>
                  </label>
                  {formData.sendEmail && formData.dueDate && (
                    <button
                      type="button"
                      className="preview-email-btn"
                      onClick={async () => {
                        console.log('🔍 Preview button clicked', {
                          sessionId: formData.sessionId,
                          sessionIds: formData.sessionIds,
                          selectedSession: selectedSession?.id,
                          availableStudentsCount: availableStudents.length
                        });
                        
                        // Determine which sessions to use for loading students
                        let sessionIdsToUse = [];
                        if (formData.sessionIds && formData.sessionIds.length > 0) {
                          sessionIdsToUse = formData.sessionIds;
                        } else if (formData.sessionId) {
                          sessionIdsToUse = [formData.sessionId];
                        }
                        
                        // Load students from all selected sessions
                        if (sessionIdsToUse.length > 0) {
                          if (sessionIdsToUse.length === 1) {
                            // Single session - use existing function
                            console.log(`📚 Loading students for single session: ${sessionIdsToUse[0]}`);
                            await loadSessionStudents(sessionIdsToUse[0]);
                            // Also set selectedSession for backward compatibility
                            try {
                              const session = await sessionsService.getSession(sessionIdsToUse[0]);
                              setSelectedSession(session);
                            } catch (error) {
                              console.error('Error loading session:', error);
                            }
                          } else {
                            // Multiple sessions - use new function
                            console.log(`📚 Loading students from ${sessionIdsToUse.length} sessions`);
                            await loadStudentsFromMultipleSessions(sessionIdsToUse);
                            // Set selectedSession to first session for backward compatibility
                            try {
                              const session = await sessionsService.getSession(sessionIdsToUse[0]);
                              setSelectedSession(session);
                            } catch (error) {
                              console.error('Error loading session:', error);
                            }
                          }
                        }
                        
                        // Wait a bit for state to update
                        await new Promise(resolve => setTimeout(resolve, 200));
                        
                        console.log('📊 After loading', {
                          availableStudentsCount: availableStudents.length,
                          selectedStudentsCount: selectedStudents.length,
                          sessionIdsUsed: sessionIdsToUse
                        });
                        
                        await handlePreviewEmail();
                        setShowEmailPreview(true);
                      }}
                      disabled={saving || sendingEmail}
                    >
                      👁️ Preview & Select Recipients
                    </button>
                  )}
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="save-btn" disabled={saving || sendingEmail}>
                  {saving ? (sendingEmail ? '📧 Sending emails...' : '💾 Saving...') : '💾 Save'}
                </button>
                <button type="button" onClick={resetForm} className="cancel-btn" disabled={saving || sendingEmail}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="homeworks-list">
        {loading ? (
          <div className="empty-state">
            <p>Loading homeworks...</p>
          </div>
        ) : homeworks.length === 0 ? (
          <div className="empty-state">
            <p>No homework assignments yet. Create your first homework!</p>
          </div>
        ) : (
          homeworks.map((homework) => {
            const session = homework.sessionId ? sessions.find(s => s.id === homework.sessionId) : null;
            return (
            <div key={homework.id} className="homework-card">
              <h3>{homework.title}</h3>
              {homework.description && (
                <p className="homework-desc">{homework.description}</p>
              )}
              <div className="homework-details">
                {session && (
                  <div className="detail-item">
                    <span className="detail-label">📅 Session:</span>
                    <span>{(() => {
                      const sessionNum = getSessionNumber(session);
                      return sessionNum ? `Session ${sessionNum}: ${session.title}` : session.title;
                    })()}</span>
                  </div>
                )}
                {homework.topicId && (
                  <div className="detail-item">
                    <span className="detail-label">📚 Topic:</span>
                    <span>{homework.topicId}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">⏰ Due Date:</span>
                  <span>{new Date(homework.dueDate).toLocaleString()}</span>
                </div>
              </div>
              <div className="homework-actions">
                <button onClick={() => handleViewSubmissions(homework)} className="view-submissions-btn">
                  📊 View Submissions ({homework.submissionCount || 0})
                </button>
                <button onClick={() => handleEdit(homework)} className="edit-btn">✏️ Edit</button>
                <button onClick={() => handleDelete(homework.id)} className="delete-btn">🗑️ Delete</button>
              </div>
            </div>
            );
          })
        )}
      </div>

      {/* Submissions Modal */}
      {showSubmissions && (
        <SubmissionsModal
          homework={homeworks.find(h => h.id === showSubmissions)}
          submissions={submissions}
          studentsMap={studentsMap}
          onClose={() => setShowSubmissions(null)}
          onGrade={handleGradeSubmission}
        />
      )}

      {/* Email Preview & Student Selection Modal */}
      {showEmailPreview && formData.sendEmail && formData.dueDate && (() => {
        // Get session for the modal
        const sessionForModal = selectedSession || 
          (formData.sessionId ? sessions.find(s => s.id === formData.sessionId) : null) || 
          (formData.sessionIds.length > 0 ? sessions.find(s => s.id === formData.sessionIds[0]) : null);
        
        if (!sessionForModal) {
          return null;
        }
        
        return (
          <EmailPreviewModal
            key={sessionForModal.id} // Force re-render when session changes
            homeworkTitle={formData.title}
            homeworkDescription={formData.description}
            session={sessionForModal}
            course={courses.find(c => c.id === sessionForModal.courseId)}
            dueDate={formData.dueDate}
            availableStudents={availableStudents}
            selectedStudents={selectedStudents}
          onToggleStudent={(studentId) => {
            if (selectedStudents.includes(studentId)) {
              setSelectedStudents(selectedStudents.filter(id => id !== studentId));
            } else {
              setSelectedStudents([...selectedStudents, studentId]);
            }
          }}
          onSelectAll={() => setSelectedStudents(availableStudents.map(s => s.id))}
          onDeselectAll={() => setSelectedStudents([])}
          previewHtml={previewHtml}
          emailFormData={emailFormData}
          onEmailFormDataChange={setEmailFormData}
          onPreview={() => {
            handlePreviewEmail();
          }}
          onSend={async () => {
            if (selectedStudents.length === 0) {
              alert('Please select at least one student to send the email to.');
              return;
            }
            const homeworkData = {
              title: formData.title,
              description: formData.description,
              sessionId: formData.sessionId,
              topicId: formData.topicId || null,
              dueDate: formData.dueDate
            };
            let homeworkId = editingHomework?.id;
            if (!homeworkId) {
              homeworkId = await homeworksService.createHomework({
                ...homeworkData,
                createdBy: user.id
              });
            } else {
              await homeworksService.updateHomework(homeworkId, homeworkData);
            }
            await sendHomeworkEmailToStudents(homeworkId, homeworkData);
            resetForm();
          }}
          onClose={() => {
            setShowEmailPreview(false);
            setFormData({ ...formData, sendEmail: false });
          }}
          sending={sendingEmail}
          />
        );
      })()}
    </div>
  );
};

const SubmissionsModal = ({ homework, submissions, studentsMap, onClose, onGrade }) => {
  const [grading, setGrading] = useState({});
  const [grades, setGrades] = useState({});
  const [feedbacks, setFeedbacks] = useState({});

  const handleStartGrading = (submissionId) => {
    const submission = submissions.find(s => s.id === submissionId);
    setGrading({ ...grading, [submissionId]: true });
    setGrades({ ...grades, [submissionId]: submission.grade || '' });
    setFeedbacks({ ...feedbacks, [submissionId]: submission.feedback || '' });
  };

  const handleSaveGrade = async (submissionId) => {
    const grade = parseInt(grades[submissionId]);
    if (isNaN(grade) || grade < 0 || grade > 100) {
      alert('Please enter a valid grade between 0 and 100.');
      return;
    }
    await onGrade(homework.id, submissionId, grade, feedbacks[submissionId] || '');
    setGrading({ ...grading, [submissionId]: false });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content submissions-modal" onClick={(e) => e.stopPropagation()}>
        <h2>📊 Submissions for: {homework?.title}</h2>
        {submissions.length === 0 ? (
          <p className="no-submissions">No submissions yet.</p>
        ) : (
          <div className="submissions-list">
            {submissions.map((submission) => {
              const student = studentsMap[submission.studentId];
              const isGrading = grading[submission.id];
              return (
                <div key={submission.id} className="submission-item">
                  <div className="submission-header">
                    <div>
                      <strong>{student?.name || student?.email?.split('@')[0] || 'Unknown Student'}</strong>
                      <span className="submission-date">
                        Submitted: {new Date(submission.submittedAt).toLocaleString()}
                      </span>
                    </div>
                    {submission.grade !== undefined && (
                      <span className="grade-badge">
                        Grade: {submission.grade} / 100
                      </span>
                    )}
                  </div>
                  <div className="submission-content">
                    <div className="form-group">
                      <label>Answer:</label>
                      <div className="answer-display">{submission.answer || 'No text answer'}</div>
                    </div>
                    {submission.files && submission.files.length > 0 && (
                      <div className="form-group">
                        <label>Files:</label>
                        <div className="files-list">
                          {submission.files.map((file, idx) => (
                            <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer" className="file-link">
                              📎 {file.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {isGrading ? (
                      <div className="grading-form">
                        <div className="form-group">
                          <label>Grade (0-100) *</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={grades[submission.id] || ''}
                            onChange={(e) => setGrades({ ...grades, [submission.id]: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Feedback</label>
                          <textarea
                            value={feedbacks[submission.id] || ''}
                            onChange={(e) => setFeedbacks({ ...feedbacks, [submission.id]: e.target.value })}
                            rows="3"
                            placeholder="Enter feedback for the student..."
                          />
                        </div>
                        <div className="form-actions">
                          <button
                            onClick={() => handleSaveGrade(submission.id)}
                            className="save-grade-btn"
                          >
                            💾 Save Grade
                          </button>
                          <button
                            onClick={() => setGrading({ ...grading, [submission.id]: false })}
                            className="cancel-btn"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="submission-actions">
                        {submission.feedback && (
                          <div className="feedback-display">
                            <strong>Feedback:</strong> {submission.feedback}
                          </div>
                        )}
                        <button
                          onClick={() => handleStartGrading(submission.id)}
                          className="grade-btn"
                        >
                          {submission.grade !== undefined ? '✏️ Update Grade' : '📝 Grade Submission'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="form-actions">
          <button type="button" onClick={onClose} className="cancel-btn">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const EmailPreviewModal = ({
  homeworkTitle,
  homeworkDescription,
  session,
  course,
  dueDate,
  availableStudents,
  selectedStudents,
  onToggleStudent,
  onSelectAll,
  onDeselectAll,
  previewHtml,
  emailFormData,
  onEmailFormDataChange,
  onPreview,
  onSend,
  onClose,
  sending
}) => {
  const dueDateObj = new Date(dueDate);
  const selectedCount = selectedStudents.length;
  const totalCount = availableStudents.length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content email-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="email-preview-header">
          <h2>📧 Email Preview & Recipients</h2>
          <button type="button" className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="email-preview-body">
          {/* Recipients Section */}
          <div className="recipients-section">
            <div className="recipients-header">
              <h3>📬 Select Recipients ({selectedCount} of {totalCount} selected)</h3>
              <div className="recipient-actions">
                <button type="button" onClick={onSelectAll} className="select-all-btn">
                  ✅ Select All
                </button>
                <button type="button" onClick={onDeselectAll} className="deselect-all-btn">
                  ❌ Deselect All
                </button>
              </div>
            </div>
            {totalCount === 0 && (
              <p style={{ padding: '16px', color: '#64748b', fontStyle: 'italic', textAlign: 'center' }}>
                No active students found for this session.
              </p>
            )}
            <div className="students-list" style={{ 
              maxHeight: '300px', 
              overflowY: 'auto',
              border: '2px solid #e2e8f0',
              borderRadius: '10px',
              padding: '12px',
              backgroundColor: '#f8fafc'
            }}>
              {availableStudents.length === 0 ? (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: '#64748b' 
                }}>
                  <p style={{ margin: '0', fontSize: '1rem' }}>📭 No students found</p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem' }}>
                    Make sure you've selected a session and that session has active student registrations.
                  </p>
                </div>
              ) : (
                availableStudents.map(student => (
                  <label 
                    key={student.id} 
                    className="student-checkbox-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor: selectedStudents.includes(student.id) ? '#e0e7ff' : 'white',
                      border: `2px solid ${selectedStudents.includes(student.id) ? '#6366f1' : '#e2e8f0'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!selectedStudents.includes(student.id)) {
                        e.currentTarget.style.borderColor = '#cbd5e1';
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedStudents.includes(student.id)) {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => onToggleStudent(student.id)}
                      style={{ 
                        marginRight: '12px', 
                        width: '18px', 
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                    <div className="student-info" style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: '600', 
                        color: '#1e293b',
                        marginBottom: '4px'
                      }}>
                        {student.name}
                      </div>
                      <div style={{ 
                        fontSize: '0.85rem', 
                        color: '#64748b' 
                      }}>
                        {student.email}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
            {selectedCount === 0 && (
              <p className="warning-text">⚠️ Please select at least one student to send the email.</p>
            )}
          </div>

          {/* Email Customization */}
          <div className="email-customization">
            <h3>✏️ Customize Email (Optional)</h3>
            <div className="form-group">
              <label>Custom Message</label>
              <textarea
                value={emailFormData.customMessage}
                onChange={(e) => onEmailFormDataChange({ ...emailFormData, customMessage: e.target.value })}
                rows="3"
                placeholder="Add a personal message to students (optional)..."
              />
            </div>
            <button type="button" onClick={onPreview} className="preview-btn">
              🔄 Refresh Preview
            </button>
          </div>

          {/* Email Preview */}
          <div className="email-preview-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3>👁️ Email Preview</h3>
              <small style={{ color: '#64748b', fontSize: '0.85rem' }}>
                This is how the email will appear to students
              </small>
            </div>
            <div className="preview-container" style={{ 
              border: '2px solid #e2e8f0', 
              borderRadius: '12px', 
              overflow: 'hidden',
              backgroundColor: '#f8fafc',
              minHeight: '500px'
            }}>
              <iframe
                title="Email Preview"
                srcDoc={previewHtml}
                frameBorder="0"
                className="email-preview-iframe"
                style={{ width: '100%', minHeight: '500px', border: 'none' }}
              />
            </div>
            <div style={{ 
              marginTop: '12px', 
              padding: '12px', 
              backgroundColor: '#f0f9ff', 
              borderRadius: '8px',
              border: '2px solid #bae6fd'
            }}>
              <p style={{ margin: '0', fontSize: '0.9rem', color: '#1e40af' }}>
                <strong>📋 Email includes:</strong> Homework details, due date, step-by-step instructions on how to submit (7 steps), and a direct "View & Submit Homework" button that links to the student sessions page.
              </p>
            </div>
          </div>
        </div>

        <div className="email-preview-footer">
          <button type="button" onClick={onClose} className="cancel-btn" disabled={sending}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onSend}
            className="send-email-btn"
            disabled={sending || selectedCount === 0}
          >
            {sending ? '📧 Sending...' : `✉️ Send to ${selectedCount} Student${selectedCount !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomeworkManagement;

